/**
 * Pure rules of the branch-save flow (no I/O, no clock): what a poll body means, how long to wait between polls,
 * what an R3 `package.branch_save_running` asks the client to do, and how to settle a save whose answer was lost
 * (a 502/504/network cut). The server owns every one of these facts (journal rows, R3); this file only reads them.
 * Decisions use status + `error_code` only, never message text.
 */
import { errorCodeOf } from "./api";
import type { BranchSavePoll, BranchSaveRecord, BranchSaveRunningView, PackageView, SaveBranchOut } from "./types";

/** First poll gap and the cap the exponential backoff grows to. */
export const POLL_INITIAL_MS = 2_000;
export const POLL_MAX_MS = 10_000;

/** Delay before poll number `attempt` (0-based): exponential, capped at `POLL_MAX_MS`. */
export function nextPollDelayMs(attempt: number): number {
  return Math.min(POLL_MAX_MS, Math.round(POLL_INITIAL_MS * 1.5 ** Math.max(0, attempt)));
}

export type PollOutcome =
  | { kind: "running"; chunksWritten: number }
  | { kind: "done"; out: SaveBranchOut }
  /** The result of a failed save is the body the synchronous route would have answered with, at `http_status`. */
  | { kind: "failed"; status: number; code: string | null; result: unknown }
  | { kind: "interrupted"; chunksWritten: number };

/** One poll body → what it means. An unknown `state` is treated as still running (the loop is bounded by its own cap). */
export function classifyPoll(poll: BranchSavePoll): PollOutcome {
  if (poll.state === "done") {
    const status = poll.http_status ?? 200;
    if (status < 400 && poll.result) return { kind: "done", out: poll.result as unknown as SaveBranchOut };
    return { kind: "failed", status: status >= 400 ? status : 500, code: errorCodeOf(poll.result), result: poll.result };
  }
  if (poll.state === "failed") {
    const status = poll.http_status ?? 500;
    return { kind: "failed", status: status >= 400 ? status : 500, code: errorCodeOf(poll.result), result: poll.result };
  }
  if (poll.state === "interrupted") return { kind: "interrupted", chunksWritten: poll.chunks_written ?? 0 };
  return { kind: "running", chunksWritten: poll.chunks_written ?? 0 };
}

export type ResumeAction =
  | { action: "poll"; saveId: string }
  /** A synchronous save (chat seat or small route save): nothing to poll; frames / R3 refetches say when it ends. */
  | { action: "wait_sync" }
  | { action: "idle" };

/** What R3 `package.branch_save_running` asks of the client. `null`/absent → Save is free. */
export function resumeFrom(running: BranchSaveRunningView | null | undefined): ResumeAction {
  if (!running) return { action: "idle" };
  if (running.mode === "async" && running.save_id) return { action: "poll", saveId: running.save_id };
  return { action: "wait_sync" };
}

export type CutVerdict =
  | { kind: "running"; running: BranchSaveRunningView }
  /** R3 history rows are grouped per save (MSA `group_saves`) and `files_count` grows while the save runs: a newer row proves some files reached the branch, not that the save completed. */
  | { kind: "wrote_some"; record: BranchSaveRecord }
  | { kind: "failed" };

/**
 * A save POST whose answer was lost (502/504/network). `before` = the newest `branch_saves` row when the click happened
 * (null when none); `after` = R3's package now; `branch` = the branch the click wrote to. A running save → still saving;
 * a different newest row on that branch → `wrote_some` (never "landed": rows are partial while a save runs, and another tab or the chat
 * seat may have written it); otherwise it failed. Compares rows, not clocks, so browser/server clock skew cannot flip the verdict.
 */
export function deriveAfterCut(before: BranchSaveRecord | null, after: PackageView | null | undefined, branch: string): CutVerdict {
  const running = after?.branch_save_running;
  if (running) return { kind: "running", running };
  const head = after?.branch_saves?.[0] ?? null;
  if (head && head.branch === branch && (!before || head.at !== before.at || head.version !== before.version || head.branch !== before.branch)) {
    return { kind: "wrote_some", record: head };
  }
  return { kind: "failed" };
}

/** What the store's `saveBranch` settles into (a failure throws). */
export type SaveBranchSettled =
  | { kind: "saved"; out: SaveBranchOut }
  /** The answer was lost and R3 shows a newer history row: at least `files_count` files reached the branch; completion is unknown. */
  | { kind: "wrote_some"; record: BranchSaveRecord }
  /** The answer was lost and R3 says the save is still running; the store follows it. */
  | { kind: "still_running"; running: BranchSaveRunningView };

/**
 * A save POST whose answer never came back. Only two shapes are proxy-made: a fetch rejection (`TypeError`) and a 502/504
 * with no `error_code`. A coded 502 is MSA's own answer (e.g. a peer 5xx) and is not "lost". Our typed poll errors
 * (interrupted / gone) are plain errors and never match.
 */
export function isLostAnswer(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  if (!err || typeof err !== "object") return false;
  const e = err as { status?: unknown; code?: unknown };
  return (e.status === 502 || e.status === 504) && (e.code === null || e.code === undefined);
}
